import { useState, useRef, useCallback } from 'react'
import { X } from 'lucide-react'

interface TagsInputProps {
  label: string
  values: string[]
  onChange: (values: string[]) => void
  helpText?: string
  placeholder?: string
  disabled?: boolean
}

/**
 * Pill-style tag input. Enter or comma adds a tag; backspace on empty input
 * removes the last; pasting a comma-separated string splits and adds. Dedup is
 * case-insensitive.
 */
export function TagsInput({
  label,
  values,
  onChange,
  helpText,
  placeholder = 'Type and press Enter or comma...',
  disabled,
}: TagsInputProps) {
  const [inputValue, setInputValue] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  const addTags = useCallback(
    (raw: string) => {
      const seen = new Set(values.map((v) => v.toLowerCase()))
      const next = raw
        .split(',')
        .map((s) => s.trim())
        .filter((s) => s.length > 0)
        .filter((s) => {
          const lower = s.toLowerCase()
          if (seen.has(lower)) return false
          seen.add(lower)
          return true
        })
      if (next.length > 0) onChange([...values, ...next])
      setInputValue('')
    },
    [values, onChange]
  )

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      if (inputValue.trim()) addTags(inputValue)
    } else if (e.key === 'Backspace' && !inputValue && values.length > 0) {
      onChange(values.slice(0, -1))
    }
  }

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData('text')
    if (text.includes(',')) {
      e.preventDefault()
      addTags(text)
    }
  }

  const handleBlur = () => {
    if (inputValue.trim()) addTags(inputValue)
  }

  return (
    <div>
      <label className="block text-sm font-medium mb-1">{label}</label>
      <div
        className="flex flex-wrap gap-1.5 min-h-[38px] px-3 py-1.5 border rounded-md text-sm focus-within:ring-2 focus-within:ring-primary cursor-text"
        onClick={() => inputRef.current?.focus()}
      >
        {values.map((value) => (
          <span key={value} className="inline-flex items-center gap-1 px-2 py-0.5 bg-muted rounded-md text-sm">
            {value}
            {!disabled && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  const target = value.toLowerCase()
                  onChange(values.filter((v) => v.toLowerCase() !== target))
                }}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </span>
        ))}
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onBlur={handleBlur}
          className="flex-1 min-w-[120px] outline-none bg-transparent py-0.5"
          placeholder={values.length === 0 ? placeholder : ''}
          disabled={disabled}
          autoComplete="off"
          data-1p-ignore
        />
      </div>
      {helpText && <p className="mt-1 text-xs text-muted-foreground">{helpText}</p>}
    </div>
  )
}
